import React from "react";
import { useSelector } from "react-redux";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useImmer } from "use-immer";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import { GraphConfig } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { AxisTick } from "@src/features/analytics/baseDashboard/chartComponents/AxisTick";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { InteractiveChartLegend } from "@src/features/analytics/legends/InteractiveChartLegend";
import { TimeSeriesTooltip } from "@src/features/analytics/metricDetails/timeSeries/TimeSeriesComponentHelpers";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import { StackedAreaMetricData } from "@src/types/analytics";

interface IgStackedAreaChartProps {
  config: GraphConfig;
  groupMetricData: StackedAreaMetricData[];
  graphHeight?: number;
}

export const IgStackedAreaChart = ({
  config,
  groupMetricData,
  graphHeight,
}: IgStackedAreaChartProps) => {
  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();
  const theme = useTheme();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const [focusGroups, setFocusGroups] = useImmer<string[]>([]);

  const handleLegendClick = (dataKey: string) => {
    if (focusGroups.includes(dataKey)) {
      setFocusGroups(focusGroups.filter((el) => el !== dataKey));
    } else {
      setFocusGroups((prev) => [...prev, dataKey]);
    }
  };
  const dataMin = "dataMin";
  const colorMap = variations.reduce((acc, variation, index) => {
    acc[variation.id] = TimeSeriesColors[index];
    return acc;
  }, {});

  const calculateIntervalByDuration = (duration) => {
    const granularity = duration < 5 ? "HOUR" : duration < 21 ? "DAY" : "WEEK";

    if (granularity === "HOUR") {
      const estimatedPoints = duration * 24;
      return Math.floor(estimatedPoints / 12) - 1;
    } else if (granularity === "DAY") {
      return Math.max(0, Math.floor(duration / 8) - 1);
    } else {
      const estimatedWeeks = Math.ceil(duration / 7);
      return Math.max(0, Math.floor(estimatedWeeks / 10) - 1);
    }
  };

  if (storeCurrencyConfig && storeCurrencyConfig.value) {
    return (
      <ResponsiveContainer width="100%" height={graphHeight ?? 300}>
        <AreaChart
          data={groupMetricData}
          style={{ marginLeft: -24 }}
          margin={{ top: 5, bottom: 5 }}
        >
          <CartesianGrid
            style={{ stroke: theme.igBorders.core.disabled }}
            vertical={false}
          />
          <XAxis
            dataKey={"xAxisKey"}
            axisLine={{ stroke: theme.igBorders.core.disabled }}
            tickLine={false}
            tick={(props) => (
              <AxisTick
                config={config}
                graphValueFormatter={graphValueFormatter}
                groupMetricData={groupMetricData}
                {...props}
              />
            )}
            interval={calculateIntervalByDuration(config.intervalOfTheData)}
          />
          <YAxis
            domain={[dataMin, "dataMax"]}
            style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
            axisLine={{ stroke: theme.igBorders.core.disabled }}
            tickLine={false}
            tickFormatter={(value: any) =>
              graphValueFormatter({
                isAxisTick: true,
                value,
                configDataTypes: config.dataTypes,
              })!
            }
          />
          <Tooltip
            content={<TimeSeriesTooltip colorMap={colorMap} config={config} />}
          />
          {variations.map((variation, index) => (
            <Area
              hide={focusGroups.includes(variation.id)}
              fillOpacity={0.3}
              key={`${variation.id}-area`}
              type="monotone"
              dataKey={variation.id}
              stackId="1"
              stroke={TimeSeriesColors[index]}
              fill={TimeSeriesColors[index]}
            />
          ))}
          {variations.map((variation, index) => (
            <Line
              hide={focusGroups.includes(variation.id)}
              type="monotone"
              key={`${variation.id}-line`}
              dataKey={variation.id}
              stroke={TimeSeriesColors[index]}
              dot={false}
              connectNulls={false}
              animationDuration={700}
              strokeWidth={2}
            />
          ))}
          <Legend
            wrapperStyle={{ marginLeft: "40px", paddingTop: "24px" }}
            content={
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={focusGroups}
                lines={variations.map((variation) => {
                  return { id: variation.id, name: variation.name };
                })}
              />
            }
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  } else {
    return null;
  }
};
